<template>
    <template-editor-page-layout
        :is-loading="isLoading"
        @save="save"
        @save-new="newTemplateDialog = true"
        @change-language="changeLanguage"
    >
        <template v-slot:appBar="{isNewTemplate}">
            <v-text-field
                v-model="info"
                :label="$t('admin.templateInfo')"
                min-width="350"
                density="compact"
                class="mr-4"
            />
            <v-text-field
                v-model="subjectTemplate"
                :label="$t('template_subject')"
                min-width="350"
                density="compact"
                class="mr-4"
            />
            <v-select
                v-model="documentLanguage"
                :items="templateLocales"
                :disabled="isNewTemplate || isLoading"
                max-width="80"
                density="compact"
                class="mr-4"
                @update:model-value="changeLanguage"
            />
            <div class="mr-4">
                <email-editor-placeholders-dialog
                    :type="template.notificationType"
                    :recipient-type="template.recipientType"
                />
            </div>
        </template>
        <keep-alive>
            <email-editor ref="editor" class="flex-grow-1" :options="editorOptions" @load="editorLoaded" />
        </keep-alive>
        <new-template-dialog v-model="newTemplateDialog" :template="template" :is-loading="isLoading" @save="saveNew">
            <v-select
                v-model="template.recipientType"
                :items="recipientTypeList"
                :label="`${$t('admin.recipient_type')}*`"
                :rules="[requiredRule('notification_type')]"
                density="compact"
            />
            <v-text-field
                v-model="template.info"
                :label="$t('admin.templateInfo')"
                :rules="[requiredRule('admin.templateInfo')]"
                density="compact"
            />
        </new-template-dialog>
    </template-editor-page-layout>
</template>

<script lang="ts">
    import {Component, mixins} from 'vue-facing-decorator'
    import {EmailEditor} from 'vue-email-editor'
    import TemplateEditorPageLayout from '@/components/admin/TemplateEditorPageLayout.vue'
    import {TemplateEditorPageMixin} from '@/components/account/mixins/TemplateEditorPageMixin.vue'
    import EmailEditorPlaceholdersDialog from '@/pages/admin/EmailEditorPlaceholdersDialog.vue'
    import {FormValidationRulesMixin} from '@/mixins/FormValidationRulesMixin.vue'
    import NewTemplateDialog from '@/components/admin/modals/NewTemplateDialog.vue'

    const blankTemplate = () => ({
        info: null,
        notificationType: 'CUSTOM',
        localeCode: null,
        recipientType: null,
        subjectTemplate: null,
        templateLocales: [],
    })

    @Component({
        components: {
            TemplateEditorPageLayout,
            EmailEditor,
            EmailEditorPlaceholdersDialog,
            NewTemplateDialog,
        },
    })
    export class EmailEditorPage extends mixins(TemplateEditorPageMixin, FormValidationRulesMixin) {
        template = blankTemplate()
        valid = false
        newTemplateDialog = false
        isLoading = false
        documentLanguage = ''
        subjectTemplate = ''
        info = ''
        isNewTemplate = false

        async mounted() {
            this.isLoading = true
            if (!this.$route.params.id) {
                this.isNewTemplate = true
                this.template.localeCode = 'en'
                this.documentLanguage = 'en'
                this.isLoading = false
            }
        }

        beforeUnmount() {
            this.$refs.editor = null
        }

        async loadEmailTemplate(data = {}) {
            const {localeCode, id} = data

            try {
                const data = await this.$api.notificationTemplates.get({
                    ...((localeCode || this.locale) && {localeCode: localeCode || this.locale}),
                    ...(id && {id}),
                })
                this.template = data.templates[0]
                this.subjectTemplate = this.template.subjectTemplate
                this.info = this.template.info
            } catch (e) {
                throw createError({statusCode: 404})
            }
        }

        async editorLoaded() {
            if (!this.$route.params.id) return
            this.isLoading = true
            const template = adminRuntimeStore.notificationTemplates.find(
                template => template.id === this.$route.params.id
            )
            if (template) {
                this.template = template
            } else await this.loadEmailTemplate({locale: this.locale, id: +this.$route.params.id})
            this.documentLanguage = this.template.templateLocales.includes(this.locale) ? this.locale : 'en'
            this.subjectTemplate = this.template.subjectTemplate || null
            this.info = this.template.info || null
            this.$refs.editor.loadDesign(JSON.parse(this.template.jsonTemplate))
            this.isLoading = false
        }

        async save() {
            this.isLoading = true
            await this.$refs.editor.exportHtml(async data => {
                await this.saveTemplate({
                    jsonTemplate: JSON.stringify(data.design),
                    htmlTemplate: data.html,
                    info: this.info,
                    subjectTemplate: this.subjectTemplate,
                    localeCode: this.documentLanguage,
                })
            })
            this.isLoading = false
        }

        async saveNew() {
            this.isLoading = true
            await this.$refs.editor.exportHtml(async data => {
                await this.saveNewTemplate({
                    ...this.template,
                    jsonTemplate: JSON.stringify(data.design),
                    htmlTemplate: data.html,
                    subjectTemplate: this.subjectTemplate,
                })
                await this.$router.push({name: 'emailTemplates'})
            })
            this.isLoading = false
        }

        async changeLanguage(lang) {
            this.isLoading = true
            this.template.localeCode = lang
            await this.loadEmailTemplate(this.template)
            this.$refs.editor.loadDesign(JSON.parse(this.template.jsonTemplate))
            this.isLoading = false
        }

        get recipientTypeList() {
            const list = ['B2C', 'SUPPLIER', 'COMPANY', 'AGENT_DISTRIBUTOR']

            return list.map(val => ({value: val, title: this.$t(`admin.recipientType.${val}`)}))
        }

        get templateLocales() {
            return this.$conf.public.locales
        }

        get locale() {
            return this.$i18n.locale
        }

        get templatesPageName() {
            return 'emailTemplates'
        }

        get editorOptions() {
            return {
                displayMode: 'email',
            }
        }
    }

    export default EmailEditorPage
</script>
